import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { CheckCircle, Clock, ArrowRight, Loader2, XCircle, Lock, CalendarDays, Send, Bookmark, PenLine } from "lucide-react";
import { Button } from "@/components/ui/button";
import { API } from "@/App";
import { pixel } from "@/lib/pixel";
import { BizouLogo, KissIcon } from "@/components/BizouLogo";
import { useTranslation } from "@/i18n/LanguageContext";

export default function Confirmation({ user, logout }) {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get("session_id");
  const [status, setStatus] = useState("checking");
  const [paymentInfo, setPaymentInfo] = useState(null);
  const [messageInfo, setMessageInfo] = useState(null);

  useEffect(() => {
    if (sessionId) pollPaymentStatus(sessionId);
    else setStatus("failed");
  }, [sessionId]);

  // Fetch message details after payment success
  useEffect(() => {
    if (status === "success" && user) {
      fetch(`${API}/messages`, { credentials: "include" })
        .then(res => res.ok ? res.json() : [])
        .then(msgs => {
          const reserved = msgs.find(m => m.status === "reserved" && m.is_reservation);
          const sealed = msgs.find(m => m.status === "scheduled" || m.status === "paid");
          if (reserved) setMessageInfo({ ...reserved, _isReservation: true });
          else if (sealed) setMessageInfo(sealed);
        })
        .catch(() => {});
    }
  }, [status, user]);

  // Auto-redirect to dashboard 6s after successful payment (gives time to read the confirmation)
  const [redirectCountdown, setRedirectCountdown] = useState(null);
  useEffect(() => {
    if (status === "success") {
      setRedirectCountdown(6);
      const tick = setInterval(() => {
        setRedirectCountdown((s) => (s !== null && s > 0 ? s - 1 : 0));
      }, 1000);
      const redir = setTimeout(() => navigate("/dashboard"), 6000);
      return () => { clearInterval(tick); clearTimeout(redir); };
    }
  }, [status, navigate]);

  const trackPurchase = (data) => {
    const price = data?.amount ? data.amount / 100 : 29;
    const msgId = data?.message_id || sessionId;
    if (window.gtag) window.gtag('event', 'purchase', { transaction_id: sessionId, value: price, currency: 'EUR', items: [{ item_name: 'BIZOU.me', price, quantity: 1 }] });
    pixel.purchase({ price, messageId: msgId });
  };

  const pollPaymentStatus = async (sid, attempts = 0) => {
    if (attempts >= 10) { setStatus("failed"); return; }
    try {
      const response = await fetch(`${API}/payment/status/${sid}`, { credentials: "include" });
      if (!response.ok) throw new Error("Failed");
      const data = await response.json();
      setPaymentInfo(data);
      if (data.payment_status === "paid") { setStatus("success"); trackPurchase(data); return; }
      else if (data.status === "expired") { setStatus("failed"); return; }
      setTimeout(() => pollPaymentStatus(sid, attempts + 1), 2000);
    } catch {
      if (attempts < 9) setTimeout(() => pollPaymentStatus(sid, attempts + 1), 2000);
      else setStatus("failed");
    }
  };

  return (
    <div className="min-h-screen bg-white flex flex-col">
      <header className="bg-white border-b border-[#F0E8EB]">
        <div className="container-bizou flex items-center justify-center h-16">
          <a href="/" className="flex items-center"><BizouLogo size="md" /></a>
        </div>
      </header>

      <main className="flex-1 flex items-center justify-center px-4 py-12">
        <div className="max-w-md w-full text-center">
          {status === "checking" && (
            <div className="animate-fade-in" data-testid="checking-status">
              <div className="w-20 h-20 rounded-full bg-[#FDF0F4] flex items-center justify-center mx-auto mb-6">
                <Loader2 className="w-10 h-10 text-[#E8175D] animate-spin" />
              </div>
              <h1 className="text-2xl md:text-3xl font-medium text-[#1C1917] mb-4" style={{ fontFamily: "'Playfair Display', serif" }}>
                {t("confirmation.checking")}
              </h1>
              <p className="text-[#78716C]">{t("confirmation.pleaseWait")}</p>
            </div>
          )}

          {status === "success" && (
            <div className="animate-fade-in" data-testid="success-status">
              {messageInfo?._isReservation ? (
                <>
                  <div className="w-24 h-24 rounded-full bg-violet-50 flex items-center justify-center mx-auto mb-6 ring-4 ring-violet-100">
                    <Bookmark className="w-11 h-11 text-violet-600" />
                  </div>
                  <h1 className="text-2xl md:text-3xl font-medium text-[#1C1917] mb-3" style={{ fontFamily: "'Playfair Display', serif" }} data-testid="sealed-title">
                    {t("confirmation.confirmedReserved")}
                  </h1>
                  <p className="text-[#78716C] mb-6" data-testid="sealed-desc">{t("confirmation.confirmedReservedDesc")}</p>
                  <div className="flex flex-col sm:flex-row gap-4">
                    <Button variant="outline" onClick={() => navigate("/dashboard")} className="btn-secondary flex-1" data-testid="go-dashboard-btn">{t("confirmation.mySpace")}</Button>
                    <Button onClick={() => navigate(`/create?edit=${messageInfo.message_id}`)} className="btn-primary flex-1" data-testid="compose-now-btn">
                      <PenLine className="w-4 h-4 mr-2" />{t("confirmation.composeNow")}
                    </Button>
                  </div>
                  {redirectCountdown !== null && redirectCountdown > 0 && (
                    <p className="text-xs text-[#78716C] mt-4" data-testid="auto-redirect-notice-reserved">
                      {t("confirmation.autoRedirect", { seconds: redirectCountdown })}
                    </p>
                  )}
                </>
              ) : (
                <>
                  <div className="w-24 h-24 rounded-full bg-emerald-50 flex items-center justify-center mx-auto mb-6 ring-4 ring-emerald-100">
                    <Lock className="w-11 h-11 text-emerald-600" />
                  </div>
                  <h1 className="text-2xl md:text-3xl font-medium text-[#1C1917] mb-3" style={{ fontFamily: "'Playfair Display', serif" }} data-testid="sealed-title">
                    {t("confirmation.confirmed")}
                  </h1>
                  <p className="text-[#78716C] mb-6" data-testid="sealed-desc">{t("confirmation.confirmedDesc")}</p>

                  {messageInfo && (
                    <div className="bg-[#FDF0F4] rounded-2xl p-6 mb-6 text-left" data-testid="sealed-details">
                      <div className="flex items-center gap-3 mb-4">
                        <div className="w-10 h-10 rounded-full bg-emerald-100 flex items-center justify-center">
                          <Lock className="w-5 h-5 text-emerald-600" />
                        </div>
                        <div>
                          <p className="font-medium text-[#1C1917]">{t("confirmation.scheduled")}</p>
                          <p className="text-sm text-[#78716C]">{t("confirmation.deliveryGuaranteed")}</p>
                        </div>
                      </div>
                      <p className="text-sm text-[#1C1917]">
                        {t("confirmation.sealedDelivery", {
                          date: new Date(messageInfo.delivery_date).toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' }),
                          recipient: messageInfo.recipient_name || messageInfo.recipient_email
                        })}
                      </p>
                    </div>
                  )}

                  {!messageInfo && (
                    <div className="card-bizou mb-6 text-left">
                      <div className="flex items-center gap-3 mb-4">
                        <div className="w-10 h-10 rounded-full bg-emerald-100 flex items-center justify-center">
                          <Lock className="w-5 h-5 text-emerald-600" />
                        </div>
                        <div>
                          <p className="font-medium text-[#1C1917]">{t("confirmation.scheduled")}</p>
                          <p className="text-sm text-[#78716C]">{t("confirmation.deliveryGuaranteed")}</p>
                        </div>
                      </div>
                      <p className="text-sm text-[#78716C]">{t("confirmation.followUp")}</p>
                    </div>
                  )}

                  <div className="flex flex-col sm:flex-row gap-4">
                    <Button variant="outline" onClick={() => navigate("/create")} className="btn-secondary flex-1" data-testid="create-another-btn">{t("confirmation.createAnother")}</Button>
                    <Button onClick={() => navigate("/dashboard")} className="btn-primary flex-1" data-testid="go-dashboard-btn">{t("confirmation.mySpace")}<ArrowRight className="ml-2 w-5 h-5" /></Button>
                  </div>
                  {redirectCountdown !== null && redirectCountdown > 0 && (
                    <p className="text-xs text-[#78716C] mt-4" data-testid="auto-redirect-notice">
                      {t("confirmation.autoRedirect", { seconds: redirectCountdown })}
                    </p>
                  )}
                </>
              )}
            </div>
          )}

          {status === "failed" && (
            <div className="animate-fade-in" data-testid="failed-status">
              <div className="w-20 h-20 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-6">
                <XCircle className="w-10 h-10 text-red-600" />
              </div>
              <h1 className="text-2xl md:text-3xl font-medium text-[#1C1917] mb-4" style={{ fontFamily: "'Playfair Display', serif" }}>{t("confirmation.failedTitle")}</h1>
              <p className="text-[#78716C] mb-8">{t("confirmation.failedDesc")}</p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Button variant="outline" onClick={() => navigate("/dashboard")} className="btn-secondary flex-1" data-testid="go-dashboard-failed-btn">{t("confirmation.mySpace")}</Button>
                <Button onClick={() => navigate("/create")} className="btn-primary flex-1" data-testid="retry-btn">{t("confirmation.retry")}</Button>
              </div>
            </div>
          )}
        </div>
      </main>

      <footer className="py-6 text-center text-sm text-[#78716C]">
        <p>{t("confirmation.question")} <a href="mailto:support@bizou.me" className="text-[#E8175D] hover:underline">support@bizou.me</a></p>
      </footer>
    </div>
  );
}
